import { ref } from 'vue';

import { useLocalStorage } from '@vueuse/core';
import { createI18n } from 'vue-i18n';

import { authService } from '@/services/auth';
import { translationService, type TranslationGroup } from '@/services/translation';

const storedLocale = useLocalStorage<string>('locale', 'en');

const i18n = createI18n({
  legacy: false,
  locale: storedLocale.value,
  fallbackLocale: 'en',
  messages: {},
});

export const availableLocalesWithNames = ref<TranslationGroup[]>([]);

export async function loadLocaleMessages(locale: string): Promise<void> {
  const result = await translationService.getTranslations(locale);

  if (!result.ok) {
    console.error(`Failed to load translations for locale: ${locale}`, result.error);
    throw new Error(result.error.message);
  }

  i18n.global.setLocaleMessage(locale, result.data);

  i18n.global.locale.value = locale;

  storedLocale.value = locale;
}

export async function loadAllTranslations(): Promise<void> {
  const result = await translationService.getAllTranslations();

  if (!result.ok) {
    console.error('Failed to load all translations', result.error);
    throw new Error(result.error.message);
  }

  availableLocalesWithNames.value = result.data;

  result.data.forEach(({ iso, translations }) => {
    i18n.global.setLocaleMessage(iso, translations);
  });

  const locales = result.data.map(t => t.iso);

  i18n.global.locale.value = storedLocale.value && locales.includes(storedLocale.value) ? storedLocale.value : 'en';
}

export async function switchLocale(locale: string): Promise<void> {
  const supportedLocales = availableLocalesWithNames.value.map(l => l.iso);
  if (supportedLocales.length > 0 && !supportedLocales.includes(locale)) {
    console.warn(`Locale "${locale}" is not supported. Falling back to "en".`);
    locale = 'en';
  }

  if (!i18n.global.availableLocales.includes(locale)) {
    await loadLocaleMessages(locale);
  } else {
    i18n.global.locale.value = locale;
    storedLocale.value = locale;
  }

  if (authService.isAuthenticated()) {
    try {
      await authService.updateLocale(locale);
    } catch (error) {
      console.error('Failed to save locale preference to backend:', error);
    }
  }
}

export default i18n;
